import React, { useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import api from "../../../services/api";
import useToast from "./utils/useToast";

const extractCampValues = (input = "") => {
  const campMatches = [];
  const regex = /camp=([^&\s]+)/g;
  let match;

  while ((match = regex.exec(input)) !== null) {
    if (match[1]) campMatches.push(match[1]);
  }

  return Array.from(new Set(campMatches)); // dedupe
};

const formatDateDisplay = (value) => {
  if (!value) return "-";

  const numeric = Number(value);
  let dateObj;

  if (Number.isFinite(numeric)) {
    dateObj = new Date(numeric > 1e12 ? numeric : numeric * 1000);
  } else {
    dateObj = new Date(value);
  }

  if (Number.isNaN(dateObj.getTime())) return String(value);

  return dateObj.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function ClicksViewer() {
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const csvRef = useRef(null);

  const totalClicks = useMemo(
    () => rows.reduce((sum, item) => sum + (Number(item.clicks) || 0), 0),
    [rows]
  );

  const fetchClicks = async () => {
    if (!query.trim()) {
      toast("error", "Enter a value to search");
      return;
    }

    const campValues = extractCampValues(query);
    if(!campValues.length){
         setRows([])
         return
        }
    setLoading(true);
    try {
      const res = await api({
        url: "/user/campaign_creation/get-clicks",
        method: "POST",
        data: {
          camp: campValues.length ? campValues[0] : '',
        },
      });

      const data = res?.data || [];

      setRows(data);
    } catch (err) {
      console.error(err);
      toast("error", "Unable to fetch clicks");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const csvData = useMemo(() => {
    const baseRows = rows.map((row) => ({
      Date: formatDateDisplay(row.date),
      Clicks: row.clicks || 0,
    }));
    return rows.length
      ? [...baseRows, { Date: "Total", Clicks: totalClicks }]
      : baseRows;
  }, [rows, totalClicks]);

  return (
    <div className="p-6 space-y-4 bg-white">
      <CSVLink
        data={csvData}
        headers={[
          { label: "Date", key: "Date" },
          { label: "Clicks", key: "Clicks" },
        ]}
        filename="clicks.csv"
        className="hidden"
        ref={csvRef}
      />
      <div>
        <label className="block text-sm font-medium mb-2">
          Search (Link IDs / URLs)
        </label>
        <textarea
          rows={2}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Paste link ids or URLs to fetch clicks..."
          className="w-full rounded-md border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          style={{ minHeight: "70px" }}
        />
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={fetchClicks}
            disabled={loading}
            className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? "Loading..." : "Fetch Clicks"}
          </button>
          <button
            type="button"
            onClick={() => csvRef.current?.link?.click()}
            disabled={!rows.length}
            className="ml-3 inline-flex items-center rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-200 disabled:opacity-60"
          >
            Download CSV
          </button>
        </div>
      </div>

      <div className="w-1/2 mx-auto">
        <div className="overflow-x-auto">
          <table className="table table-bordered">
            <thead className="bg-gray-300 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold text-gray-700">Date</th>
                <th className="px-4 py-3 font-semibold text-gray-700 text-right">
                  Clicks
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading ? (
                <tr>
                  <td
                    className="px-4 py-4 text-gray-500 text-center"
                    colSpan={2}
                  >
                    No data to show
                  </td>
                </tr>
              ) : (
                rows.map((item, idx) => (
                  <tr
                    key={`${item.date}-${idx}`}
                  >
                    <td className="px-4 py-3 text-gray-800">
                      {formatDateDisplay(item.date)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-800">
                      {item.clicks || 0}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-gray-100">
                <tr>
                  <td className="px-4 py-3 font-semibold text-gray-800">
                    Total
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-800">
                    {totalClicks}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

export default ClicksViewer;
